// ================================
// IMPORTS
// ================================
import { computed, type Ref } from "vue"
import { useAuthStore } from "@core/stores/auth.store"
import type { CompanyPlafond } from "@core/types/auth"
import {
    dernierRefusForfait,
    etatPlafond,
    messagePlafond,
    messageRefusForfait,
    texteDepassementApresAjout,
    type EtatPlafond,
    type MessagePlafond,
    type RefusForfait,
} from "./plafond-forfait.helper"

// ================================
// TYPES & INTERFACES
// ================================

/**
 * Retour du composable useCompanySourceLimits
 */
export interface UseCompanySourceLimitsReturn {
    /**
     * Nombre maximum de sources autorisées selon l'abonnement
     */
    getMaxSubscriptionSourceCount: Readonly<Ref<number>>
    /**
     * Nombre de sources actives actuellement
     */
    getActiveSourceCount: Readonly<Ref<number>>
    /**
     * Indique si la limite de sources est déjà atteinte (avant ajout)
     */
    getIsLimitReached: Readonly<Ref<boolean>>
    /**
     * Indique si l'ajout de nouvelles sources dépasserait strictement la limite
     * (un total égal au plafond n'est pas considéré comme un dépassement).
     * Fonction pure : l'appeler depuis un computed pour suivre actifs / plafond.
     * @param additionalSourcesCount - Nombre de sources supplémentaires à ajouter
     */
    getWouldExceedLimit: (additionalSourcesCount: number) => boolean
    /**
     * Plafond servi par l'API (DEV-2254), `null` tant qu'il n'est pas servi
     */
    getPlafondServi: Readonly<Ref<CompanyPlafond | null>>
    /**
     * État affiché : `absent` (repli sur le calcul historique), `sous_forfait`, `sursis`, `blocage`
     */
    getEtatPlafond: Readonly<Ref<EtatPlafond>>
    /**
     * Message du plafond servi (sursis ou blocage), `null` sous le forfait ou bloc absent
     */
    getMessagePlafond: Readonly<Ref<MessagePlafond | null>>
    /**
     * Dernier refus `forfait_atteint` (409) reçu à l'ajout, `null` sinon
     */
    getRefusForfait: Readonly<Ref<RefusForfait | null>>
    /**
     * Message de l'état bloqué rédigé à partir du dernier refus 409, `null` sinon
     */
    getMessageRefusForfait: Readonly<Ref<MessagePlafond | null>>
    /**
     * Texte quand les sources saisies dépassent les places servies, `null` sans plafond servi
     */
    getTexteDepassementApresAjout: Readonly<Ref<string | null>>
    /**
     * Oublie le dernier refus 409 (à l'ouverture d'un formulaire d'ajout)
     */
    effacerRefusForfait: () => void
}

// Rafraîchissement après un ajout : `./plafond-rafraichissement` (réexporté)
export { rafraichirPlafondServi } from "./plafond-rafraichissement"

// ================================
// COMPOSABLE
// ================================

/**
 * Composable pour gérer les limites de sources d'une entreprise.
 *
 * Quand l'API sert le bloc `plafond` (DEV-2254), le serveur décide :
 * `getIsLimitReached` = `plafond.blocage`, `getWouldExceedLimit(n)` =
 * `places_disponibles != null && n > places_disponibles`. Sinon, calcul
 * historique inchangé (forfait = somme des `metadata.sources` Stripe).
 *
 * @returns Objet contenant les computed pour les limites de sources
 */
export function useCompanySourceLimits(): UseCompanySourceLimitsReturn {
    const authStore = useAuthStore()

    // ================================
    // DONNÉES CALCULÉES
    // ================================

    /**
     * Nombre maximum de sources autorisées selon les abonnements Stripe actifs
     * Calcule la somme des métadonnées "sources" de toutes les subscriptions non annulées
     */
    const getMaxSubscriptionSourceCount = computed(() => {
        const subscriptions =
            authStore.company?.operation?.stripe?.subscriptions ??
            authStore.companyStripeSubscriptions ??
            []
        let total = 0

        for (const subscription of subscriptions) {
            // Ignorer les abonnements annulés
            if (subscription.status === "canceled") continue

            // Ajouter le nombre de sources depuis les métadonnées
            if (subscription.metadata?.sources) {
                const sourcesCount = parseInt(
                    subscription.metadata.sources as string,
                    10
                )
                if (!isNaN(sourcesCount)) {
                    total += sourcesCount
                }
            }
        }

        return total
    })

    /**
     * Nombre de sources actives actuellement
     * Récupéré depuis company.operation.count_active_source
     */
    const getActiveSourceCount = computed(() => {
        const fromCompany = (
            authStore.company?.operation as { count_active_source?: number }
        )?.count_active_source
        if (fromCompany != null) return fromCompany
        return authStore.companySourcesCount ?? 0
    })

    /**
     * Indique si la limite de sources est déjà atteinte (avant ajout de nouvelles sources)
     */
    const getPlafondServi = computed(() => authStore.companyPlafond ?? null)

    const getEtatPlafond = computed(() => etatPlafond(getPlafondServi.value))

    const getIsLimitReached = computed(() => {
        const plafond = getPlafondServi.value
        if (plafond) return plafond.blocage
        return getActiveSourceCount.value >= getMaxSubscriptionSourceCount.value
    })

    /**
     * Indique si l'ajout de nouvelles sources ferait un total strictement supérieur au plafond
     * @param additionalSourcesCount - Nombre de sources supplémentaires à ajouter
     * @returns True si actifs + ajout > max, false sinon (y compris égal au plafond)
     */
    function getWouldExceedLimit(additionalSourcesCount: number): boolean {
        const plafond = getPlafondServi.value
        if (plafond) {
            return (
                plafond.places_disponibles != null &&
                additionalSourcesCount > plafond.places_disponibles
            )
        }
        return (
            getActiveSourceCount.value + additionalSourcesCount >
            getMaxSubscriptionSourceCount.value
        )
    }

    const getMessagePlafond = computed(() =>
        messagePlafond(getPlafondServi.value)
    )

    const getRefusForfait = computed(() => dernierRefusForfait.value)

    const getMessageRefusForfait = computed(() =>
        dernierRefusForfait.value
            ? messageRefusForfait(dernierRefusForfait.value)
            : null
    )

    const getTexteDepassementApresAjout = computed(() =>
        getPlafondServi.value
            ? texteDepassementApresAjout(getPlafondServi.value)
            : null
    )

    function effacerRefusForfait(): void {
        dernierRefusForfait.value = null
    }

    return {
        getMaxSubscriptionSourceCount,
        getActiveSourceCount,
        getIsLimitReached,
        getWouldExceedLimit,
        getPlafondServi,
        getEtatPlafond,
        getMessagePlafond,
        getRefusForfait,
        getMessageRefusForfait,
        getTexteDepassementApresAjout,
        effacerRefusForfait,
    }
}
